import {Injectable} from '@angular/core';
import {AppMessage} from '../model/app-message';
import {catchError, filter, map, share, shareReplay, startWith, switchMap} from 'rxjs/operators';
import {of, Observable} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ApiRouteConfig } from '../../../config/api.route.config';
import {AttentionPointMessage} from '../model/attention-point-message';
import {TranslateService} from '@ngx-translate/core';
import {DateService} from './date.service';
import { IssueMessage } from '../model/issue-message';
import {interval, merge, Subject} from "rxjs";
import {AppConfig} from "../../../config/app.config";

@Injectable()
export class MessagingCenterService {

  constructor (
      private http: HttpClient,
      private config: ApiRouteConfig,
      private translateService: TranslateService,
      private dateService: DateService,
      private appConfig: AppConfig,
  ) {}

  // General polling tick to periodically update messages
  private readonly pollingTicks$ = interval(this.appConfig.MESSAGES_REQUEST_INTERVAL)
      .pipe(startWith(0), share());
  // Override to manually update one of the message categories
  private readonly forceUpdateMessages = new Subject<MessageType>();
  public readonly forceUpdateMessages$ = this.forceUpdateMessages.asObservable()
      .pipe(share());

  public readonly residenzNotifications$ = this.updateEventFor(MessageType.ResidenzNotifications).pipe(
      switchMap(() => this.getMessages()),
      shareReplay(),
  );

  public readonly attentionPointMessage$ = this.updateEventFor(MessageType.AttentionPointsMessages).pipe(
      switchMap(() => this.getAttentionPointsMessages()),
      shareReplay(),
  );

  public readonly issueMessages$ = this.updateEventFor(MessageType.IssueMessage).pipe(
      switchMap(() => this.getIssuesMessages()),
      shareReplay(),
  );
  public reloadMessages(type: MessageType) {
    this.forceUpdateMessages.next(type);
  }

  public getMessages(): Observable<AppMessage[]> {
    return this.http.get<any[]>(this.config.NOTIFICATIONS).pipe(
      map(messages => messages.map(message => Object.assign(new AppMessage(), message, {translateService: this.translateService}))),
      catchError(error => {
        return of([]);
      })
    );
  }

  public getAttentionPointsMessages(): Observable<AttentionPointMessage[]> {
    return this.http.get<any[]>(this.config.NOTIFICATIONS_AP).pipe(
      map(messages => messages.map(message => Object.assign(new AttentionPointMessage(), message,
        {translateService: this.translateService, dateService: this.dateService}))),
      catchError(error => {
        return of([]);
      })
    );
  }

  public getIssuesMessages(): Observable<IssueMessage[]> {
    return this.http.get<any[]>(this.config.NOTIFICATIONS_ISSUE).pipe(
      map(messages => messages.map(message => Object.assign(new IssueMessage(), message,
        {translateService: this.translateService, dateService: this.dateService}))),
      catchError(error => {
        return of([]);
      })
    );
  }

  private updateEventFor(type: MessageType) {
    return merge(
        this.pollingTicks$.pipe(),
        this.forceUpdateMessages$.pipe(filter(updateType => type === updateType),)
    );
  }
}

export enum MessageType {
  ResidenzNotifications = 'ResidenzNotifications',
  AttentionPointsMessages = 'AttentionPointsMessages',
  IssueMessage = 'IssueMessage',
}

